import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Box } from '@material-ui/core';
import { RangeInput } from '@material-ui/pickers';
import { useReportContext } from 'contexts/report/ReportContext';
import reportLocales from 'globals/locales/report';
import defaultLocales from 'globals/locales/default';
import { isSuccessState } from 'globals/utils';
import FsDefaultFallbackComponent from 'ui-elements/loaders/FsDefaultFallbackComponent';
import { useReportAccountContext } from 'contexts/report/ReportAccountContext';
import FsDateRangePicker from 'ui-elements/form-elements/FsDateRangePicker';
import FsAccountPicker from 'ui-elements/form-elements/FsAccountPicker';
import {
  HorizontalLine,
  Caption,
  ContainerBox,
  Headline,
  SectionBox,
} from './AccountFiltersSection.style-elements';

// Locales
const { headline } = reportLocales.account.filtersSection;

const AccountFiltersSection: React.FC = () => {
  const {
    report: {
      bankAccountsList: { status, getSimpleBankList, getAvailableDates },
    },
  } = useReportContext();
  const { setActiveDateRange } = useReportAccountContext();
  const [dateRange, setDateRange] = React.useState<RangeInput<Date>>([
    null,
    null,
  ]);

  const availableDates = useCallback(() => getAvailableDates(), [
    getAvailableDates,
  ]);

  const onDateChangeHandler = (dates: RangeInput<Date>): void => {
    setDateRange(dates);
    setActiveDateRange(dates);
  };

  useEffect(() => {
    if (status && availableDates) {
      setDateRange(availableDates);
    }
  }, [availableDates, status]);

  if (!isSuccessState(status))
    return (
      <FsDefaultFallbackComponent
        status={status}
        data-test="default-fallback-component"
        withShadow={false}
      />
    );

  return (
    <Box data-test="account-filters-section-component">
      <Headline>{headline}</Headline>
      <ContainerBox>
        <SectionBox>
          <Caption>{defaultLocales.bank}:</Caption>
          <FsAccountPicker
            banks={getSimpleBankList()}
            data-test="account-picker"
          />
        </SectionBox>
        <SectionBox isLast>
          <Caption>{defaultLocales.filter}:</Caption>
          <FsDateRangePicker
            dateRange={dateRange}
            onChange={onDateChangeHandler}
            data-test="date-range-picker"
          />
        </SectionBox>
      </ContainerBox>
      <HorizontalLine />
    </Box>
  );
};

export default observer(AccountFiltersSection);
